/**
 * Theme JS — Modern Wellness Product
 * Handles: gallery, variants, quantity, ATC, sticky bar, FAQ, scroll reveal
 */

(function () {
  'use strict';

  /* ─── Gallery ─── */
  function initGallery() {
    const mainImg    = document.querySelector('[data-gallery-img]');
    const mainWrap   = document.querySelector('[data-gallery-main]');
    const thumbs     = document.querySelectorAll('.gallery-thumb');
    const prevBtn    = document.querySelector('[data-main-prev]');
    const nextBtn    = document.querySelector('[data-main-next]');
    const counterEl  = document.querySelector('[data-gallery-counter]');
    const dataScript = document.querySelector('[data-gallery-images]');
    if (!mainImg) return;

    // Parse image list from JSON script tag
    let images = [];
    try { images = JSON.parse(dataScript?.textContent || '[]'); } catch {}
    let currentIdx = 0;

    mainImg.style.transition = 'opacity 150ms ease';

    function goTo(idx) {
      if (idx < 0 || idx >= images.length) return;
      currentIdx = idx;

      mainImg.style.opacity = '0';
      setTimeout(() => {
        mainImg.src = images[idx].src;
        mainImg.alt = images[idx].alt;
        mainImg.style.opacity = '1';
      }, 150);

      // Sync thumbnails
      thumbs.forEach((t, i) => t.classList.toggle('active', i === idx));
      // Scroll only the thumbnail track, not the page
      const activeThumb = thumbs[idx];
      const track = document.querySelector('[data-gallery-track]');
      if (activeThumb && track) {
        const thumbLeft = activeThumb.offsetLeft - track.offsetLeft;
        const center = thumbLeft - (track.clientWidth / 2) + (activeThumb.offsetWidth / 2);
        track.scrollTo({ left: center, behavior: 'smooth' });
      }

      // Update counter
      if (counterEl) counterEl.textContent = `${idx + 1} / ${images.length}`;
    }

    // Main image arrows
    prevBtn?.addEventListener('click', (e) => { e.preventDefault(); goTo(currentIdx - 1); });
    nextBtn?.addEventListener('click', (e) => { e.preventDefault(); goTo(currentIdx + 1); });

    // Thumbnail click
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => goTo(i));
      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTo(i); }
      });
    });

    // Thumbnail scroll arrows (if present)
    const track    = document.querySelector('[data-gallery-track]');
    const tPrev    = document.querySelector('[data-gallery-prev]');
    const tNext    = document.querySelector('[data-gallery-next]');
    if (track && tPrev && tNext) {
      tPrev.addEventListener('click', () => track.scrollBy({ left: -160, behavior: 'smooth' }));
      tNext.addEventListener('click', () => track.scrollBy({ left:  160, behavior: 'smooth' }));
    }

    // Swipe on main image (mobile)
    if (mainWrap && images.length > 1) {
      let startX = 0;
      mainWrap.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      mainWrap.addEventListener('touchend', (e) => {
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          if (diff > 0) goTo(currentIdx + 1);  // swipe left → next
          else          goTo(currentIdx - 1);  // swipe right → prev
        }
      }, { passive: true });
    }
  }

  /* ─── Purchase Toggle (one-time vs subscribe) ─── */
  function initPurchaseToggle() {
    const options = document.querySelectorAll('.purchase-option');
    const priceEl = document.querySelector('.price-current');

    options.forEach((opt) => {
      opt.addEventListener('click', () => {
        options.forEach(o => o.classList.remove('selected'));
        opt.classList.add('selected');

        const price = opt.dataset.price;
        if (price && priceEl) {
          priceEl.textContent = price;
        }
      });
    });
  }

  /* ─── Variant Buttons ─── */
  function initVariants() {
    document.querySelectorAll('.variant-options').forEach((group) => {
      group.querySelectorAll('.variant-btn:not(.sold-out)').forEach((btn) => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('selected'));
          btn.classList.add('selected');
        });
      });
    });
  }

  /* ─── Quantity Control ─── */
  function initQuantity() {
    document.querySelectorAll('.quantity-control').forEach((ctrl) => {
      const display = ctrl.querySelector('.qty-display');
      const minusBtn = ctrl.querySelector('[data-action="minus"]');
      const plusBtn  = ctrl.querySelector('[data-action="plus"]');
      if (!display) return;

      let qty = parseInt(display.value || display.textContent || 1, 10);

      function update(n) {
        qty = Math.max(1, Math.min(99, n));
        if (display.tagName === 'INPUT') display.value = qty;
        else display.textContent = qty;
      }

      minusBtn?.addEventListener('click', () => update(qty - 1));
      plusBtn?.addEventListener('click',  () => update(qty + 1));
    });
  }

  /* ─── Pack / Qty-Break Row Selectors ─── */
  function initRowSelectors(selector, selectedClass) {
    const groups = document.querySelectorAll(selector + '-group, [role="radiogroup"]');
    const cards = document.querySelectorAll(selector);
    if (!cards.length) return;

    // Find the ATC inside the nearest shared section (or fallback global)
    function findATC(card) {
      return card.closest('section')?.querySelector('.btn-atc') || document.querySelector('.btn-atc');
    }

    function selectCard(card) {
      // Only clear cards within the same parent group
      const group = card.parentElement;
      group.querySelectorAll(selector).forEach(c => {
        c.classList.remove(selectedClass);
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add(selectedClass);
      card.setAttribute('aria-checked', 'true');

      const qty       = parseInt(card.dataset.packQty || 1, 10);
      const price     = card.dataset.packPrice || '';
      const variantId = card.dataset.variantId;
      const atcBtn    = findATC(card);

      if (atcBtn) {
        atcBtn.dataset.quantity = qty;
        if (variantId && variantId !== '0') atcBtn.dataset.variantId = variantId;
        const svg = atcBtn.querySelector('svg')?.outerHTML || '';
        const label = (window.themeStrings && window.themeStrings.atcText) || 'Добави в количката';
        atcBtn.innerHTML = `${svg} ${label}`;
      }
    }

    cards.forEach(card => {
      card.addEventListener('click', () => selectCard(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectCard(card); }
        const all = [...card.parentElement.querySelectorAll(selector)];
        const idx = all.indexOf(card);
        if (e.key === 'ArrowDown' && all[idx + 1]) { all[idx + 1].focus(); selectCard(all[idx + 1]); }
        if (e.key === 'ArrowUp'   && all[idx - 1]) { all[idx - 1].focus(); selectCard(all[idx - 1]); }
      });
    });
  }

  function initPackSelector() {
    initRowSelectors('.hero-pack-card', 'selected');
    initRowSelectors('.qty-break-card', 'popular');
  }

  /* ─── Add to Cart ─── */
  function initATC() {
    document.querySelectorAll('[data-atc]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = btn.dataset.variantId || document.querySelector('[data-variant-id]')?.dataset.variantId;
        // Prefer quantity from pack selector, fall back to qty stepper
        const qty = parseInt(btn.dataset.quantity || document.querySelector('.qty-display')?.value || 1, 10);

        if (!variantId) return;

        const originalText = btn.innerHTML;
        btn.disabled = true;
        const T = window.themeStrings || {};
        btn.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
          </svg>
          ${T.atcAdding || 'Добавяне...'}`;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: qty }),
          });

          if (res.ok) {
            btn.innerHTML = `
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              ${T.atcAdded || 'Добавено!'}`;
            btn.style.background = 'linear-gradient(135deg, #059669, #047857)';
            // Open the cart drawer after adding
            if (window.cartDrawer) window.cartDrawer.open();
            else updateCartCount();
            setTimeout(() => {
              btn.innerHTML = originalText;
              btn.style.background = '';
              btn.disabled = false;
            }, 1800);
          } else {
            throw new Error('Add to cart failed');
          }
        } catch {
          btn.innerHTML = originalText;
          btn.disabled = false;
        }
      });
    });
  }

  /* ─── Cart State + Drawer ─── */
  const cartDrawer = {
    el:        () => document.getElementById('cart-drawer'),
    body:      () => document.querySelector('[data-cart-body]'),
    items:     () => document.querySelector('[data-cart-items]'),
    empty:     () => document.querySelector('[data-cart-empty]'),
    footer:    () => document.querySelector('[data-cart-footer]'),
    total:     () => document.querySelector('[data-cart-total]'),
    countPill: () => document.querySelector('[data-cart-count]'),
    template:  () => document.getElementById('cart-item-template'),

    open() {
      this.el()?.classList.add('open');
      this.el()?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-open');
      this.refresh();
    },
    close() {
      this.el()?.classList.remove('open');
      this.el()?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-open');
    },

    formatMoney(cents) {
      // Shopify stores cart prices in cents
      return new Intl.NumberFormat('bg-BG', { style: 'currency', currency: window.themeCurrency || 'EUR' }).format(cents / 100);
    },

    async refresh() {
      try {
        const res  = await fetch('/cart.js');
        const cart = await res.json();
        this.render(cart);
      } catch (e) { console.error('Cart refresh failed', e); }
    },

    render(cart) {
      const itemsEl  = this.items();
      const emptyEl  = this.empty();
      const footerEl = this.footer();
      if (!itemsEl) return;

      // Update counts everywhere
      document.querySelectorAll('.cart-count, [data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        if (el.classList.contains('cart-count')) {
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }
      });

      // Empty / non-empty
      if (cart.item_count === 0) {
        itemsEl.innerHTML = '';
        if (emptyEl)  emptyEl.style.display = 'flex';
        if (footerEl) footerEl.hidden = true;
        return;
      }
      if (emptyEl)  emptyEl.style.display = 'none';
      if (footerEl) footerEl.hidden = false;

      // Render items
      itemsEl.innerHTML = '';
      const tpl = this.template();
      cart.items.forEach((item, idx) => {
        const node = tpl.content.cloneNode(true);
        const li   = node.querySelector('[data-cart-item]');
        li.dataset.line  = idx + 1;
        li.dataset.key   = item.key;

        node.querySelectorAll('[data-cart-link]').forEach(a => a.href = item.url);
        node.querySelector('[data-cart-image]').src = item.image || '//placehold.co/80';
        node.querySelector('[data-cart-image]').alt = item.product_title;
        node.querySelector('[data-cart-title]').textContent = item.product_title;

        const variantEl = node.querySelector('[data-cart-variant]');
        if (item.variant_title && item.variant_title !== 'Default Title') {
          variantEl.textContent = item.variant_title;
        } else {
          variantEl.remove();
        }

        node.querySelector('[data-cart-qty]').textContent   = item.quantity;
        node.querySelector('[data-cart-price]').textContent = this.formatMoney(item.final_line_price);

        // Discounted line: show original crossed out + discount label
        const isDiscounted = item.original_line_price > item.final_line_price;
        if (isDiscounted) {
          const origEl = node.querySelector('[data-cart-price-original]');
          if (origEl) {
            origEl.textContent = this.formatMoney(item.original_line_price);
            origEl.hidden = false;
          }
          const discountEl = node.querySelector('[data-cart-item-discount]');
          if (discountEl && item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
            const titles = item.line_level_discount_allocations.map(d => d.discount_application.title).filter(Boolean);
            const savings = item.original_line_price - item.final_line_price;
            discountEl.innerHTML = `
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 12V8H6a2 2 0 010-4h12v4"/><path d="M4 6v12c0 1.1.9 2 2 2h14v-4"/><path d="M18 12a2 2 0 000 4h4v-4z"/></svg>
              <span>${titles.join(', ') || 'Намаление'}</span>
              <span class="cart-item-discount-amt">−${this.formatMoney(savings)}</span>
            `;
            discountEl.hidden = false;
          }
        }

        itemsEl.appendChild(node);
      });

      // Footer: discounts, savings, totals
      this.renderFooter(cart);
    },

    renderFooter(cart) {
      const totalEl       = this.total();
      const originalRowEl = document.querySelector('[data-cart-original-row]');
      const originalEl    = document.querySelector('[data-cart-original]');
      const savingsEl     = document.querySelector('[data-cart-savings]');
      const savingsAmtEl  = document.querySelector('[data-cart-savings-amt]');
      const discountsEl   = document.querySelector('[data-cart-discounts]');

      if (totalEl) totalEl.textContent = this.formatMoney(cart.total_price);

      const hasDiscount = cart.total_discount > 0;
      if (originalRowEl) originalRowEl.hidden = !hasDiscount;
      if (savingsEl)     savingsEl.hidden     = !hasDiscount;
      if (originalEl && hasDiscount) originalEl.textContent = this.formatMoney(cart.items_subtotal_price + cart.total_discount > cart.items_subtotal_price ? cart.items_subtotal_price + cart.total_discount : cart.original_total_price || cart.total_price + cart.total_discount);
      if (savingsAmtEl  && hasDiscount) savingsAmtEl.textContent = '−' + this.formatMoney(cart.total_discount);

      // Cart-level discount applications (e.g., "Spring Sale 10% off")
      if (discountsEl) {
        const apps = cart.cart_level_discount_applications || [];
        if (apps.length) {
          discountsEl.innerHTML = apps.map(d => `
            <div class="cart-discount-row">
              <span class="cart-discount-tag">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                ${d.title}
              </span>
              <span class="cart-discount-value">−${this.formatMoney(d.total_allocated_amount)}</span>
            </div>
          `).join('');
          discountsEl.hidden = false;
        } else {
          discountsEl.innerHTML = '';
          discountsEl.hidden = true;
        }
      }
    },

    async updateLine(line, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity }),
        });
        const cart = await res.json();
        this.render(cart);
      } catch {}
    },
  };

  function initCartDrawer() {
    // Open
    document.querySelectorAll('[data-cart-open]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        cartDrawer.open();
      });
    });

    // Close (backdrop, X, links inside empty state)
    document.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (btn.tagName !== 'A') e.preventDefault();
        cartDrawer.close();
      });
    });

    // Esc to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') cartDrawer.close();
    });

    // Qty + remove (delegated)
    document.addEventListener('click', (e) => {
      const item = e.target.closest('[data-cart-item]');
      if (!item) return;
      const line = parseInt(item.dataset.line, 10);
      const currentQty = parseInt(item.querySelector('[data-cart-qty]')?.textContent || 1, 10);

      if (e.target.closest('[data-cart-qty-plus]'))  cartDrawer.updateLine(line, currentQty + 1);
      if (e.target.closest('[data-cart-qty-minus]')) cartDrawer.updateLine(line, currentQty - 1);
      if (e.target.closest('[data-cart-remove]'))    cartDrawer.updateLine(line, 0);
    });
  }

  /* ─── Cart Count ─── */
  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      cartDrawer.render(cart);
    } catch {}
  }

  // expose globally so ATC can call it
  window.cartDrawer = cartDrawer;

  /* ─── Sticky ATC ─── */
  function initStickyATC() {
    const sticky  = document.querySelector('.sticky-atc');
    const mainATC = document.querySelector('.btn-atc');
    if (!sticky || !mainATC) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        sticky.classList.toggle('visible', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );
    observer.observe(mainATC);
  }

  /* ─── FAQ Accordion ─── */
  function initFAQ() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.faq-trigger');
      if (!trigger) return;
      e.preventDefault();
      const item = trigger.closest('.faq-item');
      const wasOpen = item.classList.contains('open');
      document.querySelectorAll('.faq-item.open').forEach(i => i.classList.remove('open'));
      if (!wasOpen) item.classList.add('open');
    });
  }

  /* ─── Hero Info Accordion (in product hero) ─── */
  function initHeroInfo() {
    // Event delegation — works even with dynamically loaded content / Shopify editor reloads
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.hero-info-trigger');
      if (!trigger) return;
      e.preventDefault();
      const item = trigger.closest('.hero-info-item');
      if (!item) return;
      const isOpen = item.classList.contains('open');
      item.classList.toggle('open');
      trigger.setAttribute('aria-expanded', !isOpen);
    });
  }

  /* ─── Before / After Slider ─── */
  function initBeforeAfter() {
    document.querySelectorAll('[data-ba-slider]').forEach((slider) => {
      const wrap   = slider.querySelector('.ba-slider__wrap');
      const before = slider.querySelector('[data-ba-before]');
      const handle = slider.querySelector('[data-ba-handle]');
      if (!wrap || !before || !handle) return;

      let dragging = false;

      function setPosition(x) {
        const rect = wrap.getBoundingClientRect();
        let pct = ((x - rect.left) / rect.width) * 100;
        pct = Math.max(2, Math.min(98, pct));
        before.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
        handle.style.left = pct + '%';
      }

      function onStart(e) {
        e.preventDefault();
        dragging = true;
        wrap.style.cursor = 'col-resize';
      }
      function onMove(e) {
        if (!dragging) return;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        setPosition(clientX);
      }
      function onEnd() {
        dragging = false;
        wrap.style.cursor = '';
      }

      // Mouse
      handle.addEventListener('mousedown', onStart);
      wrap.addEventListener('mousedown', (e) => { dragging = true; onMove(e); });
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);

      // Touch
      handle.addEventListener('touchstart', onStart, { passive: false });
      wrap.addEventListener('touchstart', (e) => { dragging = true; onMove(e); }, { passive: true });
      window.addEventListener('touchmove', (e) => { if (dragging) { e.preventDefault(); onMove(e); } }, { passive: false });
      window.addEventListener('touchend', onEnd);

      // Keyboard (accessibility)
      const grip = handle.querySelector('.ba-slider__handle-grip');
      grip?.addEventListener('keydown', (e) => {
        const rect = wrap.getBoundingClientRect();
        const current = parseFloat(before.style.width) || 50;
        const step = 3;
        if (e.key === 'ArrowLeft')  { setPosition(rect.left + rect.width * (current - step) / 100); e.preventDefault(); }
        if (e.key === 'ArrowRight') { setPosition(rect.left + rect.width * (current + step) / 100); e.preventDefault(); }
      });
    });
  }

  /* ─── Ingredients Carousel ─── */
  function initIngredientsCarousel() {
    const track = document.querySelector('[data-ingredients-track]');
    const prevBtn = document.querySelector('[data-ingredients-prev]');
    const nextBtn = document.querySelector('[data-ingredients-next]');
    const dotsWrap = document.querySelector('[data-ingredients-dots]');
    if (!track) return;

    const cards = [...track.querySelectorAll('.ingredient-card')];
    if (cards.length <= 2) {
      // Not enough cards for carousel — hide arrows/dots
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
      if (dotsWrap) dotsWrap.style.display = 'none';
      return;
    }

    // How many visible at once
    function visibleCount() { return window.innerWidth < 640 ? 1 : 2; }

    // Total pages
    function pageCount() { return Math.ceil(cards.length / visibleCount()); }

    // Current page (based on scroll)
    function currentPage() {
      if (!cards[0]) return 0;
      const cardW = cards[0].offsetWidth + 16; // gap
      return Math.round(track.scrollLeft / (cardW * visibleCount()));
    }

    // Scroll to page
    function goToPage(p) {
      const cardW = cards[0].offsetWidth + 16;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const target = Math.min(p * cardW * visibleCount(), maxScroll);
      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    // Build dots
    function buildDots() {
      if (!dotsWrap) return;
      const total = pageCount();
      dotsWrap.innerHTML = '';
      for (let i = 0; i < total; i++) {
        const dot = document.createElement('button');
        dot.className = 'ingredients-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', `Страница ${i + 1}`);
        dot.addEventListener('click', () => goToPage(i));
        dotsWrap.appendChild(dot);
      }
    }

    function updateState() {
      const page = currentPage();
      const total = pageCount();

      if (prevBtn) prevBtn.disabled = page <= 0;
      if (nextBtn) nextBtn.disabled = page >= total - 1;

      if (dotsWrap) {
        dotsWrap.querySelectorAll('.ingredients-dot').forEach((d, i) => {
          d.classList.toggle('active', i === page);
        });
      }
    }

    prevBtn?.addEventListener('click', () => goToPage(currentPage() - 1));
    nextBtn?.addEventListener('click', () => goToPage(currentPage() + 1));
    track.addEventListener('scroll', () => requestAnimationFrame(updateState), { passive: true });

    buildDots();
    updateState();

    // Rebuild on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { buildDots(); updateState(); }, 200);
    });
  }

  /* ─── Scroll Reveal ─── */
  function initScrollReveal() {
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach(el => observer.observe(el));
  }

  /* ─── Header scroll shadow + mobile menu ─── */
  function initHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    window.addEventListener('scroll', () => {
      header.classList.toggle('scrolled', window.scrollY > 10);
    }, { passive: true });

    // Mobile hamburger menu
    const toggle = document.querySelector('[data-menu-toggle]');
    const menu   = document.getElementById('mobile-menu');
    if (!toggle || !menu) return;

    toggle.addEventListener('click', () => {
      const isOpen = menu.classList.toggle('open');
      toggle.classList.toggle('open', isOpen);
      toggle.setAttribute('aria-expanded', isOpen);
      menu.setAttribute('aria-hidden', !isOpen);
    });

    // Close menu when a link is clicked
    menu.querySelectorAll('[data-menu-close]').forEach(link => {
      link.addEventListener('click', () => {
        menu.classList.remove('open');
        toggle.classList.remove('open');
        toggle.setAttribute('aria-expanded', 'false');
        menu.setAttribute('aria-hidden', 'true');
      });
    });
  }

  /* ─── Init all ─── */
  function init() {
    initCartDrawer();
    initPackSelector();
    initGallery();
    initPurchaseToggle();
    initVariants();
    initQuantity();
    initATC();
    initStickyATC();
    initFAQ();
    initHeroInfo();
    initBeforeAfter();
    initIngredientsCarousel();
    initScrollReveal();
    initHeader();
    updateCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init pack selector on Shopify editor section reload
  document.addEventListener('shopify:section:load', () => {
    initPackSelector();
    initScrollReveal();
    // accordions use delegation, no re-init needed
  });
})();
